/**
 * What: Renders a New page that owns its own `<s-page>` (Tests, Settings) alongside the real nav menu and activity banner, bypassing ShopifyMainPageView's own
 *   `<s-page>` wrapper. Mirrors the existing precedent for the results view (ShopifyResultsView) —
 *   two nested `<s-page>` elements has never been exercised in this codebase, so self-wrapping New
 *   pages get the same bypass instead.
 * Transport / data-flow: Pure layout composition; no data of its own.
 */
import { DirectStudyResults } from './direct-studies/DirectStudyResults'
import type { ReactNode } from 'react'
import type { FeatureEntitlementSnapshot } from '@testfeed/entitlement-contracts'
import type { useAppBridge } from '@shopify/app-bridge-react'
import { useBannerDismiss } from '../hooks/useBannerDismiss'
import {
  ShopifyAppContext,
  type ShopifyAppContextValue,
  type VerifiedTestFeedConnectionScope,
} from '../ShopifyAppContext'
import { ShopifyStudyActivityProvider } from '../ShopifyStudyActivityContext'
import type { ShopifyAppViewState, ShopifyStudyBuilderStep } from '../resultsNavigation'
import type { TestFeedConnectionStatus } from '../typesStore'
import { HomeRoute } from './app-home/HomeRoute'
import { InsightsRoute } from './insights/InsightsRoute'
import type { TestInsightSetup } from './insights/InsightSetupsSection'
import { IntegrationsRoute } from './integrations/IntegrationsRoute'
import { StudySetupsRoute } from './StudySetupsRoute'
import { StudyBuilderRoute } from './study-builder/StudyBuilderRoute'
import { studyBuilderVisitKey } from '../hooks/study-builder/studyBuilderStart'
import { SupportRoute } from './SupportRoute'
import { isRetiredStudyView, RetiredStudyPage } from './RetiredStudyPage'
import { StudyRunRoute } from './StudyRunRoute'
import { ShopifyAppNavigation } from './ShopifyAppNavigation'
import { ShopifyPageContent } from './ShopifyPageContent'
import { ShopifyStudyActivityBanner } from './ShopifyStudyActivityBanner'

const SELF_PAGED_VIEWS = [
  'studies',
  'results',
  'study-builder',
  'study-run',
  'direct-study-results',
  'support',
  'settings-integrations',
  'home',
  'insights',
] as const

/**
 * Tests, Settings, Home and Insights
 * each own their own `<s-page>` (the New pages standard), so they render inside
 * ShopifySelfPagedView instead of nesting inside ShopifyMainPageView's `<s-page>`. A button with
 * slot="primary-action"/"secondary-actions" only reaches the real page header if it is a direct
 * child of the `<s-page>` that owns that header — nested two `<s-page>` deep (as these views were
 * before) silently drops it into the body instead.
 * Returns null for any other view.
 */
export function renderShopifyNewPagesRoute(options: {
  appView: ShopifyAppViewState
  shopContextValue: ShopifyAppContextValue
  shopify: ReturnType<typeof useAppBridge>
  verifiedScope: VerifiedTestFeedConnectionScope | null
  entitlements: FeatureEntitlementSnapshot
  onOpenResults: (studyId: string, runId: string | null) => void
  showConnectedBanner: boolean
  onDismissConnectedBanner: () => void
  connectedBannerTitle: string
  connectedBannerBody: string
  connectionRecoverySection: ReactNode
  onCreateTest: () => void
  isTestFeedConnected: boolean
  onConnectTestFeed: () => void
  navigateToStudyBuilder: (options?: { studyId?: string; draftId?: string; step?: ShopifyStudyBuilderStep }) => void
  navigateToInsights: () => void
  navigateToStudies: () => void
  navigateToSettings: () => void
  navigateToDirectResults: (studyId: string, runId: string | null) => void
  onTestSetup?: TestInsightSetup
  /** Shop the Study Builder route mounts its own builder for. */
  expectedShop: string | null
  /** Called by the Study Builder when a prepared setup is saved. */
  onOpenDirectResults?: (studyId: string, runId: string | null) => void
  onStudyPrepared?: (studyId: string) => void
  onBackToStudies: () => void
  readiness: TestFeedConnectionStatus | null
  isLoadingReadiness: boolean
}): ReactNode | null {
  const { appView } = options
  const retired = isRetiredStudyView(appView)
  // A new draft, setup or shop is a new visit: the page remounts and starts clean (no hand-made resets).
  const visit = appView.view === 'study-builder' ? studyBuilderVisitKey(options.verifiedScope, appView) : ''
  if (!(SELF_PAGED_VIEWS as readonly string[]).includes(appView.view)) return null
  let integrationPage: ReactNode = null
  if (appView.view === 'settings-integrations')
    integrationPage = <IntegrationsRoute shopify={options.shopify} isTestFeedConnected={options.isTestFeedConnected} />
  return (
    <ShopifySelfPagedView
      shopContextValue={options.shopContextValue}
      shopify={options.shopify}
      verifiedScope={options.verifiedScope}
      entitlements={options.entitlements}
      onOpenResults={options.onOpenResults}
      showConnectedBanner={options.showConnectedBanner}
      onDismissConnectedBanner={options.onDismissConnectedBanner}
      connectedBannerTitle={options.connectedBannerTitle}
      connectedBannerBody={options.connectedBannerBody}
      connectionRecoverySection={options.connectionRecoverySection}
      // /tests shows running tests and every test's status itself; the shared unread banner (with its mark-read
      // dismiss) would reintroduce read/unread behavior there, so only that route suppresses it.
      showStudyActivityBanner={appView.view !== 'studies'}
    >
      {retired && <RetiredStudyPage onBack={options.onBackToStudies} />}
      {integrationPage}
      {appView.view === 'direct-study-results' && (
        <DirectStudyResults
          key={`${options.shopContextValue.shopDomain}:${options.shopContextValue.orgId}:${options.shopContextValue.connectionId}:${appView.studyId}:${appView.runId}`}
          shopify={options.shopify}
          studyId={appView.studyId}
          runId={appView.runId}
          questionSelection={appView.questionSelection}
          onBack={options.onBackToStudies}
        />
      )}
      {appView.view === 'studies' && (
        <StudySetupsRoute
          shopify={options.shopify}
          verifiedScope={options.verifiedScope}
          isTestFeedConnected={options.isTestFeedConnected}
          readiness={options.readiness}
          isLoadingReadiness={options.isLoadingReadiness}
          onCreateTest={options.onCreateTest}
          onResumeDraft={(draftId, studyId, status, runId) => {
            if (studyId && runId) options.onOpenDirectResults?.(studyId, runId)
            else if (studyId && status === 'ready') options.onStudyPrepared?.(studyId)
            else options.navigateToStudyBuilder(studyId ? { studyId } : { draftId, step: 'survey' })
          }}
        />
      )}
      {appView.view === 'study-builder' && !retired && (
        <StudyBuilderRoute
          key={visit}
          visit={visit}
          appView={appView}
          shopify={options.shopify}
          expectedShop={options.expectedShop}
          verifiedScope={options.verifiedScope}
          readiness={options.readiness}
          onStudyPrepared={options.onStudyPrepared}
          onBack={options.onBackToStudies}
        />
      )}
      {appView.view === 'support' && !retired && <SupportRoute appView={appView} shopify={options.shopify} />}
      {appView.view === 'study-run' && !retired && (
        <StudyRunRoute appView={appView} shopify={options.shopify} onBackToStudies={options.onBackToStudies} />
      )}
      {appView.view === 'home' && (
        <HomeRoute
          shopify={options.shopify}
          isTestFeedConnected={options.isTestFeedConnected}
          onConnect={options.onConnectTestFeed}
          storeScope={options.verifiedScope}
          navigateToStudyBuilder={options.navigateToStudyBuilder}
          navigateToInsights={options.navigateToInsights}
          navigateToStudies={options.navigateToStudies}
          navigateToSettings={options.navigateToSettings}
          navigateToDirectResults={options.navigateToDirectResults}
        />
      )}
      {appView.view === 'insights' && <InsightsRoute onTestSetup={options.onTestSetup} />}
    </ShopifySelfPagedView>
  )
}

export function ShopifySelfPagedView({
  shopContextValue,
  shopify,
  verifiedScope,
  entitlements,
  onOpenResults,
  showConnectedBanner,
  onDismissConnectedBanner,
  connectedBannerTitle,
  connectedBannerBody,
  connectionRecoverySection,
  showStudyActivityBanner = true,
  children,
}: ShopifySelfPagedViewProps) {
  const connectedBannerRef = useBannerDismiss(showConnectedBanner, onDismissConnectedBanner)
  return (
    <ShopifyAppContext.Provider value={shopContextValue}>
      <ShopifyStudyActivityProvider shopify={shopify} scope={verifiedScope} onOpenResults={onOpenResults}>
        <ShopifyAppNavigation entitlements={entitlements} />
        <ShopifyPageContent>
          {showStudyActivityBanner && <ShopifyStudyActivityBanner />}
          {showConnectedBanner && (
            <s-banner ref={connectedBannerRef} heading={connectedBannerTitle} tone="success" dismissible>
              <s-text>{connectedBannerBody}</s-text>
            </s-banner>
          )}
          {connectionRecoverySection}
          {children}
        </ShopifyPageContent>
      </ShopifyStudyActivityProvider>
    </ShopifyAppContext.Provider>
  )
}

interface ShopifySelfPagedViewProps {
  shopContextValue: ShopifyAppContextValue
  shopify: ReturnType<typeof useAppBridge>
  verifiedScope: VerifiedTestFeedConnectionScope | null
  entitlements: FeatureEntitlementSnapshot
  onOpenResults: (studyId: string, runId: string | null) => void
  showConnectedBanner: boolean
  onDismissConnectedBanner: () => void
  connectedBannerTitle: string
  connectedBannerBody: string
  connectionRecoverySection: ReactNode
  /** Defaults to true. The Tests index sets false: it has no unread or mark-read behavior. */
  showStudyActivityBanner?: boolean
  children: ReactNode
}
